import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { ModalProps } from '../../../services/modals.service';

import { calculateSmsCount, SMS_LIMIT } from './sms-sender';
import { RouterInput, trpc } from '../../../api/trpc';
import { useModalClose } from '../../../utils/useModalClose';
import { InputWrapper } from '../../common/input-wrapper';
import { AlertService } from '../../../services/alert.service';
import useSWR, { KeyedMutator } from 'swr';
import useSWRMutation from 'swr/mutation';

interface AddTemplateModalProps extends ModalProps {
  idTemplate?: string;
  mutate?: KeyedMutator<
    {
      name: string;
      id: string;
      createdAt: Date;
      updatedAt: Date;
      text: string;
    }[]
  >;
}

type FormValues = {
  name: string;
  text: string;
};

export const SmsTemplateModal: React.FC<AddTemplateModalProps> = ({
  close,
  reject,
  idTemplate,
  mutate,
}) => {
  const modalRef = useModalClose(reject);

  const { data: template } = useSWR(
    idTemplate ? ['smsRouter.template.getById', idTemplate] : null,
    async ([, id]: [string, string]) =>
      trpc.smsRouter.template.getById.query({ id }),
  );

  const { trigger: create, isMutating: isCreateMutating } = useSWRMutation(
    ['smsRouter.template.create'],
    (_key, { arg }: { arg: RouterInput['smsRouter']['template']['create'] }) =>
      trpc.smsRouter.template.create.mutate(arg),
  );
  const { trigger: update, isMutating: isUpdateMutating } = useSWRMutation(
    ['smsRouter.template.update'],
    (_key, { arg }: { arg: RouterInput['smsRouter']['template']['update'] }) =>
      trpc.smsRouter.template.update.mutate(arg),
  );

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { name: '', text: '' },
  });

  useEffect(() => {
    if (template) {
      setValue('name', template.name);
      setValue('text', template.text);
    }
  }, [template, setValue]);

  const text = watch('text') || '';
  const smsCount = calculateSmsCount(text.length);
  const loading = isCreateMutating || isUpdateMutating;

  const onSave = async (data: FormValues) => {
    try {
      if (idTemplate) {
        await update({ id: idTemplate, name: data.name, text: data.text });
      } else {
        await create({ name: data.name, text: data.text });
      }
      await mutate?.();
      close(data);
    } catch (e) {
      AlertService.error(
        (e as Error).message || 'Ошибка при сохранении шаблона',
      );
    }
  };

  return (
    <div className="modal-dialog" ref={modalRef} style={{ maxWidth: 500 }}>
      <form className="modal-content" onSubmit={handleSubmit(onSave)}>
        <div className="modal-header">
          <h5 className="modal-title">
            {idTemplate ? 'Редактировать шаблон' : 'Создать шаблон'}
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => reject?.()}
            aria-label="Закрыть"
          />
        </div>

        <div className="modal-body">
          <InputWrapper
            title="Название"
            className="mb-3"
            error={errors.name?.message}
            required
          >
            <div className="input-group">
              <input
                className="form-control"
                placeholder="Название шаблона"
                {...register('name', { required: 'Введите название' })}
              />
            </div>
          </InputWrapper>

          <InputWrapper
            title="Текст шаблона"
            className="mb-3"
            error={errors.text?.message}
            required
          >
            <div className="input-group">
              <textarea
                className="form-control"
                rows={6}
                placeholder="Введите текст шаблона"
                {...register('text', { required: 'Введите текст' })}
              />
            </div>
          </InputWrapper>

          <label className="form-label">
            Текст длина: {text.length} / {SMS_LIMIT} символов · {smsCount} смс
          </label>
        </div>

        <div className="modal-footer justify-content-end">
          <button type="submit" className="btn btn-success" disabled={loading}>
            {loading ? 'Сохраняем...' : 'Сохранить'}
          </button>
        </div>
      </form>
    </div>
  );
};
